// src/components/MessagesContent.jsx
// Reusable messages content component - can be shown below any journey bar
import React, { useState, useEffect } from 'react';
import { fetchAdminNotifications } from '../services/adminNotificationService';
import { 
  MessageSquare, 
  Search,
  Info,
  CheckCircle,
  AlertTriangle,
  Mail,
  MailOpen,
  X
} from 'lucide-react';

// Severity icons and colors
const SEVERITY_CONFIG = {
  'info': {
    icon: Info,
    bgColor: 'bg-blue-50',
    iconBgColor: 'bg-blue-100',
    iconColor: 'text-blue-600',
    textColor: 'text-blue-900',
    borderColor: 'ring-blue-200'
  },
  'success': {
    icon: CheckCircle,
    bgColor: 'bg-green-50',
    iconBgColor: 'bg-green-100',
    iconColor: 'text-green-600',
    textColor: 'text-green-900',
    borderColor: 'ring-green-200'
  },
  'warn': {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50',
    iconBgColor: 'bg-amber-100',
    iconColor: 'text-amber-600',
    textColor: 'text-amber-900',
    borderColor: 'ring-amber-200'
  }
};

export function MessagesContent() {
  // State management
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRead, setFilterRead] = useState('all'); // 'all', 'unread', 'read'
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Fetch admin intake notifications (tenant 300 for now)
  useEffect(() => {
    async function loadMessages() {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAdminNotifications();
        
        if (response.success) {
          setMessages(response.data);
        } else {
          throw new Error('Failed to load messages');
        }
      } catch (err) {
        console.error('Error loading messages:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    loadMessages();
  }, []);
  
  // Get unique categories
  const categories = ['all', ...new Set(messages.map(msg => msg.category))];
  
  // Filter messages
  const filteredMessages = messages.filter(msg => {
    const matchesSearch = 
      msg.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.body.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRead = 
      filterRead === 'all' || 
      (filterRead === 'unread' && !msg.read) ||
      (filterRead === 'read' && msg.read);
    
    const matchesCategory = filterCategory === 'all' || msg.category === filterCategory;
    
    return matchesSearch && matchesRead && matchesCategory;
  });
  
  // Count unread
  const unreadCount = messages.filter(msg => !msg.read).length;
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };
  
  // Mark message as read
  const markAsRead = (messageId) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      )
    );
  };
  
  // Delete message
  const deleteMessage = (messageId) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };
  
  return (
    <div className="space-y-6">
      {/* Header with unread count */}
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-bold text-[#18365E]">Messages</h2>
        {unreadCount > 0 && (
          <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-bold text-white bg-[#FF6E4C]">
            {unreadCount}
          </span>
        )}
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#e5e7eb] bg-white text-[#18365E] placeholder-[#717182] focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent transition-all"
          />
        </div>
        
        {/* Read/Unread Filter */}
        <select
          value={filterRead}
          onChange={(e) => setFilterRead(e.target.value)}
          className="px-4 py-3 rounded-xl border border-[#e5e7eb] bg-white text-[#18365E] focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent transition-all font-medium text-sm"
        >
          <option value="all">All Messages</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>
        
        {/* Category Filter */}
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-3 rounded-xl border border-[#e5e7eb] bg-white text-[#18365E] focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-transparent transition-all font-medium text-sm"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-2xl ring-1 ring-[#e5e7eb] bg-white p-6 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#F5F7FA] flex-shrink-0" />
                <div className="flex-1 space-y-3">
                  <div className="h-5 bg-[#F5F7FA] rounded w-3/4" />
                  <div className="h-4 bg-[#F5F7FA] rounded w-full" />
                  <div className="h-4 bg-[#F5F7FA] rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="rounded-2xl ring-1 ring-red-200 bg-red-50 p-6 text-center">
          <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to load messages</h3>
          <p className="text-sm text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition-all"
          >
            Try Again
          </button>
        </div>
      )}
      
      {/* Empty State */}
      {!loading && !error && filteredMessages.length === 0 && (
        <div className="rounded-2xl ring-1 ring-[#e5e7eb] bg-white p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#18365E]/10 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-[#18365E]" />
          </div>
          <h3 className="text-xl font-semibold text-[#18365E] mb-2">
            {searchQuery || filterRead !== 'all' || filterCategory !== 'all' 
              ? 'No messages found' 
              : 'No messages yet'}
          </h3>
          <p className="text-sm text-[#717182] font-medium mb-6 max-w-md mx-auto">
            {searchQuery || filterRead !== 'all' || filterCategory !== 'all'
              ? 'Try adjusting your search or filters.'
              : 'You\'re all caught up! New messages will appear here.'}
          </p>
          {(searchQuery || filterRead !== 'all' || filterCategory !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterRead('all');
                setFilterCategory('all');
              }}
              className="inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm text-[#18365E] bg-white ring-1 ring-[#e5e7eb] hover:ring-[#18365E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#18365E] transition-all"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
      
      {/* Messages List */}
      {!loading && !error && filteredMessages.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#717182] font-medium">
              Showing {filteredMessages.length} {filteredMessages.length === 1 ? 'message' : 'messages'}
            </p>
          </div>
          
          <div className="space-y-3">
            {filteredMessages.map((msg) => {
              const config = SEVERITY_CONFIG[msg.severity];
              const Icon = config.icon;
              const ReadIcon = msg.read ? MailOpen : Mail;
              
              return (
                <div
                  key={msg.id}
                  className={`rounded-2xl ring-1 ${config.borderColor} ${config.bgColor} p-6 transition-all ${
                    !msg.read ? 'shadow-sm' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl ${config.iconBgColor} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${config.iconColor}`} />
                    </div>
                    
                    {/* Message Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex items-center gap-2 flex-1">
                          <h3 className={`text-base font-semibold ${config.textColor}`}>
                            {msg.title}
                          </h3>
                          {!msg.read && (
                            <span className="w-2 h-2 rounded-full bg-[#FF6E4C]" aria-label="Unread" />
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!msg.read && (
                            <button
                              onClick={() => markAsRead(msg.id)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#717182] hover:text-[#18365E] hover:bg-white/50 focus:outline-none focus:ring-2 focus:ring-[#18365E] transition-all"
                              aria-label="Mark as read"
                              title="Mark as read"
                            >
                              <ReadIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteMessage(msg.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#717182] hover:text-red-600 hover:bg-white/50 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all"
                            aria-label="Delete message"
                            title="Delete message"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <p className={`text-sm ${config.textColor} opacity-90 mb-3`}>
                        {msg.body}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs font-medium opacity-75">
                        <span className={config.textColor}>{formatDate(msg.createdAt)}</span>
                        <span>•</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-md bg-white/50 ${config.textColor}`}>
                          {msg.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}